import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Plus, Trash2, CheckCircle, Clock, Pencil,
  Calendar, User, X, Mail, MessageSquare, Send
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-800 border-amber-200' },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
};

/* ── Inline Notes Component ── */
const TaskNotes = ({ task, onNoteAdded }) => {
  const [expanded, setExpanded] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const notes = task.notes || [];

  const addNote = async () => {
    if (!noteText.trim() || submitting) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/tasks/${task.id}/notes`, { content: noteText });
      onNoteAdded(task.id, res.data);
      setNoteText('');
    } catch (e) {
      console.error('Error adding note:', e);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/tasks/${task.id}/notes/${noteId}`);
      onNoteAdded(task.id, null, noteId);
    } catch (e) {
      console.error('Error deleting note:', e);
    }
  };

  return (
    <div className="mt-2">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors"
        data-testid={`toggle-notes-${task.id}`}
      >
        <MessageSquare className="h-3 w-3" />
        <span>{notes.length > 0 ? `${notes.length} note${notes.length !== 1 ? 's' : ''}` : 'Add note'}</span>
      </button>

      {expanded && (
        <div className="mt-2 space-y-2 pl-1 border-l-2 border-slate-200 ml-1">
          {notes.map(note => (
            <div key={note.id} className="pl-3 py-1 group" data-testid={`note-${note.id}`}>
              <div className="flex items-start justify-between gap-2">
                <p className="text-xs text-slate-700 whitespace-pre-wrap">{note.content}</p>
                {(note.author_id === user?.id) && (
                  <button
                    onClick={() => deleteNote(note.id)}
                    className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500 transition-all flex-shrink-0"
                    data-testid={`delete-note-${note.id}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
              <p className="text-[10px] text-slate-400 mt-0.5">
                {note.author_name} &middot; {new Date(note.created_at).toLocaleString()}
              </p>
            </div>
          ))}

          {/* Add note input */}
          <div className="flex items-center gap-1.5 pl-3" data-testid={`add-note-form-${task.id}`}>
            <input
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addNote(); } }}
              placeholder="Add a note..."
              className="flex-1 text-xs border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-slate-400 text-slate-800"
              data-testid={`note-input-${task.id}`}
            />
            <button
              onClick={addNote}
              disabled={!noteText.trim() || submitting}
              className="text-slate-400 hover:text-[#081120] disabled:opacity-30 p-1 transition-colors"
              data-testid={`send-note-${task.id}`}
            >
              <Send className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Main TaskManager Component ── */
const TaskManager = ({ onTaskCountChange }) => {
  const { selectedRole, user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    title: '', description: '', due_date: '',
    assigned_to: [], application_id: '', notify_assignees: false
  });

  const isAdmin = selectedRole === 'admin';
  const isUnderwriter = selectedRole === 'underwriter';
  const isBroker = selectedRole === 'broker';
  const canAssign = isAdmin || isUnderwriter || isBroker;

  const pendingCount = tasks.filter(t => t.status === 'pending').length;

  useEffect(() => {
    if (onTaskCountChange) onTaskCountChange(pendingCount);
  }, [pendingCount, onTaskCountChange]);

  useEffect(() => {
    fetchTasks();
    if (canAssign) fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTasks = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/tasks`);
      setTasks(res.data.tasks || []);
    } catch (e) {
      console.error('Error fetching tasks:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      if (isBroker) {
        const res = await axios.get(`${BACKEND_URL}/api/broker/team-assignees`);
        const data = res.data || {};
        const teamList = [...(data.sub_brokers || []), ...(data.underwriters || [])];
        // Prepend the broker themselves so they can self-assign
        const list = user
          ? [{ id: user.id, name: `${user.name || user.email} (Me)`, email: user.email, roles: ['broker'] }, ...teamList]
          : teamList;
        setUsers(list);
      } else {
        const res = await axios.get(`${BACKEND_URL}/api/users`);
        setUsers(res.data || []);
      }
    } catch (e) {
      console.error('Error fetching users:', e);
    }
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) return;
    try {
      const payload = {
        ...form,
        assigned_to: form.assigned_to.length > 0 ? form.assigned_to : undefined,
      };
      if (editingId) {
        await axios.put(`${BACKEND_URL}/api/tasks/${editingId}`, payload);
        fetchTasks();
      } else {
        const res = await axios.post(`${BACKEND_URL}/api/tasks`, payload);
        setTasks(prev => [res.data, ...prev]);
      }
      resetForm();
    } catch (e) {
      console.error('Error saving task:', e);
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await axios.put(`${BACKEND_URL}/api/tasks/${taskId}`, { status: newStatus });
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
    } catch (e) {
      console.error('Error updating task status:', e);
    }
  };

  const handleDelete = async (taskId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/tasks/${taskId}`);
      setTasks(prev => prev.filter(t => t.id !== taskId));
    } catch (e) {
      console.error('Error deleting task:', e);
    }
  };

  const handleNoteUpdate = (taskId, newNote, deleteNoteId) => {
    setTasks(prev => prev.map(t => {
      if (t.id !== taskId) return t;
      const notes = t.notes || [];
      if (deleteNoteId) {
        return { ...t, notes: notes.filter(n => n.id !== deleteNoteId) };
      }
      if (newNote) {
        return { ...t, notes: [...notes, newNote] };
      }
      return t;
    }));
  };

  const startEdit = (task) => {
    const assignedTo = Array.isArray(task.assigned_to) ? task.assigned_to : (task.assigned_to ? [task.assigned_to] : []);
    // Convert any due_date format into the YYYY-MM-DDTHH:MM expected by datetime-local input
    let dueLocal = '';
    if (task.due_date) {
      const d = new Date(task.due_date);
      if (!isNaN(d.getTime())) {
        const pad = (n) => String(n).padStart(2, '0');
        dueLocal = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      } else {
        dueLocal = task.due_date;
      }
    }
    setForm({
      title: task.title,
      description: task.description || '',
      due_date: dueLocal,
      assigned_to: assignedTo,
      application_id: task.application_id || '',
      notify_assignees: false,
    });
    setEditingId(task.id);
    setShowForm(true);
  };

  const resetForm = () => {
    setForm({ title: '', description: '', due_date: '', assigned_to: [], application_id: '', notify_assignees: false });
    setEditingId(null);
    setShowForm(false);
  };

  const toggleAssignee = (userId) => {
    setForm(prev => {
      const current = prev.assigned_to || [];
      if (current.includes(userId)) {
        return { ...prev, assigned_to: current.filter(id => id !== userId) };
      }
      return { ...prev, assigned_to: [...current, userId] };
    });
  };

  const filtered = tasks.filter(t => {
    if (filter === 'all') return true;
    return t.status === filter;
  }).sort((a, b) => {
    if (a.status === 'completed' && b.status !== 'completed') return 1;
    if (a.status !== 'completed' && b.status === 'completed') return -1;
    return 0;
  });

  const inProgressCount = tasks.filter(t => t.status === 'in_progress').length;
  const completedCount = tasks.filter(t => t.status === 'completed').length;
  const overdueCount = tasks.filter(t => t.due_date && new Date(t.due_date) < new Date() && t.status !== 'completed').length;

  const isOverdue = (task) => task.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed';

  const getAssigneeNames = (task) => {
    if (task.assigned_to_names && task.assigned_to_names.length > 0) {
      return task.assigned_to_names.join(', ');
    }
    return task.assigned_to_name || 'Unassigned';
  };

  const selectedUserNames = form.assigned_to
    .map(id => users.find(u => u.id === id)?.name)
    .filter(Boolean);

  return (
    <div className="space-y-4" data-testid="task-manager">
      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <button onClick={() => setFilter('pending')} className={`p-3 rounded-lg border text-left transition-colors ${filter === 'pending' ? 'border-amber-400 bg-amber-50' : 'border-slate-200 hover:border-slate-300'}`} data-testid="filter-pending">
          <p className="text-xs text-slate-500">Pending</p>
          <p className="text-xl font-bold text-amber-600">{pendingCount}</p>
        </button>
        <button onClick={() => setFilter('in_progress')} className={`p-3 rounded-lg border text-left transition-colors ${filter === 'in_progress' ? 'border-blue-400 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`} data-testid="filter-in-progress">
          <p className="text-xs text-slate-500">In Progress</p>
          <p className="text-xl font-bold text-blue-600">{inProgressCount}</p>
        </button>
        <button onClick={() => setFilter('completed')} className={`p-3 rounded-lg border text-left transition-colors ${filter === 'completed' ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`} data-testid="filter-completed">
          <p className="text-xs text-slate-500">Completed</p>
          <p className="text-xl font-bold text-emerald-600">{completedCount}</p>
        </button>
        <button onClick={() => setFilter('all')} className={`p-3 rounded-lg border text-left transition-colors ${filter === 'all' ? 'border-slate-400 bg-slate-50' : 'border-slate-200 hover:border-slate-300'}`} data-testid="filter-all">
          <p className="text-xs text-slate-500">All Tasks</p>
          <p className="text-xl font-bold text-slate-700">{tasks.length}</p>
          {overdueCount > 0 && <p className="text-xs text-red-500 font-medium">{overdueCount} overdue</p>}
        </button>
      </div>

      {/* New task button / form */}
      {!showForm ? (
        <Button onClick={() => setShowForm(true)} className="bg-[#081120] hover:bg-[#0E333F] text-white" data-testid="new-task-btn">
          <Plus className="h-4 w-4 mr-1" /> New Task
        </Button>
      ) : (
        <Card className="border-slate-300">
          <CardContent className="pt-4 space-y-3">
            <div>
              <Label className="text-xs">Title *</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Task title" data-testid="task-title-input" />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <textarea
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Optional details..."
                className="w-full min-h-[50px] p-2 border border-slate-200 rounded-md text-sm resize-none focus:outline-none focus:ring-1 focus:ring-slate-400 text-slate-800"
                data-testid="task-description-input"
              />
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Due Date &amp; Time</Label>
                <Input type="datetime-local" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} className="max-w-xs" data-testid="task-due-date-input" />
              </div>
              {canAssign && (
                <div>
                  <Label className="text-xs">Assign To (select multiple)</Label>
                  <div className="border border-slate-200 rounded-md max-h-[140px] overflow-y-auto" data-testid="task-assign-multi">
                    {users.map(u => (
                      <label
                        key={u.id}
                        className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50 ${
                          form.assigned_to.includes(u.id) ? 'bg-blue-50 text-blue-800' : 'text-slate-700'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={form.assigned_to.includes(u.id)}
                          onChange={() => toggleAssignee(u.id)}
                          className="rounded border-slate-300"
                        />
                        <span className="truncate">{u.name}</span>
                        <span className="text-[10px] text-slate-400 ml-auto flex-shrink-0">{u.roles?.join(', ')}</span>
                      </label>
                    ))}
                  </div>
                  {selectedUserNames.length > 0 && (
                    <p className="text-xs text-slate-500 mt-1">Selected: {selectedUserNames.join(', ')}</p>
                  )}
                </div>
              )}
            </div>
            {canAssign && form.assigned_to.length > 0 && (
              <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700" data-testid="task-notify-checkbox">
                <input
                  type="checkbox"
                  checked={form.notify_assignees}
                  onChange={e => setForm({ ...form, notify_assignees: e.target.checked })}
                  className="rounded border-slate-300"
                />
                <Mail className="h-3.5 w-3.5 text-slate-500" />
                <span>Send email notification to assignee(s)</span>
              </label>
            )}
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={resetForm}>Cancel</Button>
              <Button size="sm" onClick={handleSubmit} disabled={!form.title.trim()} className="bg-[#081120] hover:bg-[#0E333F] text-white" data-testid="save-task-btn">
                {editingId ? 'Update Task' : 'Create Task'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tasks list */}
      {loading ? (
        <p className="text-sm text-slate-400 text-center py-4">Loading tasks...</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-8">No tasks {filter !== 'all' ? `with status "${filter.replace('_', ' ')}"` : 'yet'}</p>
      ) : (
        <div className="space-y-2">
          {filtered.map(task => (
            <div
              key={task.id}
              className={`p-3 rounded-lg border transition-all ${
                task.status === 'completed' ? 'border-emerald-100 bg-emerald-50/30' :
                isOverdue(task) ? 'border-red-200 bg-red-50/50' :
                'border-slate-200 bg-white hover:border-slate-300'
              }`}
              data-testid={`task-${task.id}`}
            >
              <div className="flex items-start gap-3">
                {/* Status toggle */}
                <button
                  onClick={() => handleStatusChange(task.id, task.status === 'completed' ? 'pending' : task.status === 'pending' ? 'in_progress' : 'completed')}
                  className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                    task.status === 'completed' ? 'border-emerald-500 bg-emerald-500 text-white' :
                    task.status === 'in_progress' ? 'border-blue-400 bg-blue-100' :
                    'border-slate-300 hover:border-slate-400'
                  }`}
                  title="Toggle status"
                  data-testid={`toggle-status-${task.id}`}
                >
                  {task.status === 'completed' && <CheckCircle className="h-3 w-3" />}
                  {task.status === 'in_progress' && <Clock className="h-3 w-3 text-blue-600" />}
                </button>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`text-sm font-medium ${task.status === 'completed' ? 'line-through text-slate-400' : 'text-slate-800'}`}>{task.title}</p>
                    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${STATUS_CONFIG[task.status]?.color || ''}`}>
                      {STATUS_CONFIG[task.status]?.label || task.status}
                    </Badge>
                    {isOverdue(task) && <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-red-100 text-red-700 border-red-200">Overdue</Badge>}
                  </div>
                  {task.description && <p className="text-xs text-slate-500 mt-0.5">{task.description}</p>}
                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                    {task.due_date && (() => {
                      const raw = String(task.due_date);
                      const hasTime = raw.includes('T') || /\d{1,2}:\d{2}/.test(raw);
                      const d = new Date(raw);
                      const valid = !isNaN(d.getTime());
                      const text = valid
                        ? (hasTime
                            ? `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
                            : d.toLocaleDateString())
                        : raw;
                      return (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {text}
                        </span>
                      );
                    })()}
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {getAssigneeNames(task)}
                    </span>
                  </div>

                  {/* Notes section */}
                  <TaskNotes task={task} onNoteAdded={handleNoteUpdate} />
                </div>

                {/* Actions */}
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => startEdit(task)} className="text-slate-400 hover:text-slate-600 p-1" data-testid={`edit-task-${task.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => handleDelete(task.id)} className="text-slate-400 hover:text-red-500 p-1" data-testid={`delete-task-${task.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TaskManager;
